import React, { useEffect, useState, useMemo } from 'react';
import { hasPermission, getToken } from '../services/authService';
import API_BASE_URL from '../config';
import { useNavigate } from 'react-router-dom';
import { useCurrency } from '../context/CurrencyContext';
import './Admin.css';

function formatDate(d) {
  const dt = new Date(d);
  return `${dt.getMonth() + 1}/${dt.getDate()}`;
}

function formatDuration(seconds) {
  if (seconds === 0 || isNaN(seconds)) return 'N/A';
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  if (mins < 60) return `${mins}m ${secs}s`;
  const hrs = Math.floor(mins / 60);
  const remainingMins = mins % 60;
  return `${hrs}h ${remainingMins}m`;
}

function BarChart({ data = [], width = 700, height = 220 }) {
  const { formatPrice } = useCurrency();
  if (!data.length) return null;
  const max = Math.max(...data.map(d => d.value), 1);
  const padding = 20;
  // compute a chartWidth that grows with number of bars but allows responsive display
  const chartWidth = Math.max(300, data.length * 48 + padding * 2);
  const innerW = chartWidth - padding * 2;
  const innerH = height - padding * 2;
  const barWidth = innerW / data.length * 0.7;
  const gap = innerW / data.length * 0.3;

  return (
    <svg viewBox={`0 0 ${chartWidth} ${height}`} width="100%" height={height} preserveAspectRatio="xMinYMin meet" style={{ background: 'linear-gradient(180deg,#fff,#f8fbff)', borderRadius: 8, border: '1px solid #e2e8f0' }}>
      <defs>
        <linearGradient id="g1" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.9" />
          <stop offset="100%" stopColor="#0ea5e9" stopOpacity="0.6" />
        </linearGradient>
      </defs>
      {data.map((d, i) => {
        const x = padding + i * (barWidth + gap) + gap / 2;
        const h = (d.value / max) * innerH;
        const y = padding + (innerH - h);
        return (
          <g key={d.label}>
            <rect x={x} y={y} width={barWidth} height={h} rx={4} fill="url(#g1)">
              <title>{`${d.label}: ${formatPrice(d.value)}`}</title>
            </rect>
            <text x={x + barWidth / 2} y={padding + innerH + 14} fontSize={10} fill="#64748b" textAnchor="middle" style={{ fontWeight: 500 }}>{d.label}</text>
          </g>
        );
      })}
    </svg>
  );
}

function ChatVolumeChart({ data = [], height = 240 }) {
  if (!data.length) return <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>No trend data available for this range.</div>;
  const max = Math.max(...data.map(d => Math.max(d.answered, d.missed, d.offline, 1)), 1);
  const padding = 30;
  const chartWidth = Math.max(500, data.length * 70 + padding * 2);
  const innerW = chartWidth - padding * 2;
  const innerH = height - padding * 2;
  const groupWidth = innerW / data.length * 0.75;
  const gap = innerW / data.length * 0.25;
  const barWidth = groupWidth / 3 - 2; // three bars: answered, missed, offline

  return (
    <div style={{ overflowX: 'auto', width: '100%' }}>
      <svg viewBox={`0 0 ${chartWidth} ${height}`} width="100%" height={height} preserveAspectRatio="xMinYMin meet" style={{ background: 'linear-gradient(180deg,#fff,#f8fafc)', borderRadius: 12, border: '1px solid #e2e8f0', minWidth: chartWidth }}>
        <defs>
          <linearGradient id="answeredGrad" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#10b981" stopOpacity="0.95" />
            <stop offset="100%" stopColor="#059669" stopOpacity="0.8" />
          </linearGradient>
          <linearGradient id="missedGrad" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#f43f5e" stopOpacity="0.95" />
            <stop offset="100%" stopColor="#e11d48" stopOpacity="0.8" />
          </linearGradient>
          <linearGradient id="offlineGrad" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#f59e0b" stopOpacity="0.95" />
            <stop offset="100%" stopColor="#d97706" stopOpacity="0.8" />
          </linearGradient>
        </defs>
        {/* Grid lines */}
        {[0, 0.25, 0.5, 0.75, 1].map((p, idx) => {
          const y = padding + (1 - p) * innerH;
          return (
            <g key={idx}>
              <line x1={padding} y1={y} x2={chartWidth - padding} y2={y} stroke="#f1f5f9" strokeWidth="1" />
              <text x={padding - 8} y={y + 3} fontSize={10} fill="#94a3b8" textAnchor="end">{Math.round(p * max)}</text>
            </g>
          );
        })}
        {data.map((d, i) => {
          const xGroup = padding + i * (groupWidth + gap) + gap / 2;
          const hAns = (d.answered / max) * innerH;
          const hMis = (d.missed / max) * innerH;
          const hOff = (d.offline / max) * innerH;

          const yAns = padding + (innerH - hAns);
          const yMis = padding + (innerH - hMis);
          const yOff = padding + (innerH - hOff);

          const xAns = xGroup;
          const xMis = xGroup + barWidth + 2;
          const xOff = xGroup + (barWidth + 2) * 2;

          return (
            <g key={d.date}>
              {/* Answered Bar */}
              {d.answered > 0 ? (
                <rect x={xAns} y={yAns} width={barWidth} height={hAns} rx={2} fill="url(#answeredGrad)">
                  <title>{`Date: ${d.date}\nAnswered Chats: ${d.answered}`}</title>
                </rect>
              ) : null}
              {/* Missed Bar */}
              {d.missed > 0 ? (
                <rect x={xMis} y={yMis} width={barWidth} height={hMis} rx={2} fill="url(#missedGrad)">
                  <title>{`Date: ${d.date}\nMissed Chats: ${d.missed}`}</title>
                </rect>
              ) : null}
              {/* Offline Bar */}
              {d.offline > 0 ? (
                <rect x={xOff} y={yOff} width={barWidth} height={hOff} rx={2} fill="url(#offlineGrad)">
                  <title>{`Date: ${d.date}\nOffline Chats (Leads): ${d.offline}`}</title>
                </rect>
              ) : null}
              {/* Label */}
              <text x={xGroup + groupWidth / 2} y={padding + innerH + 16} fontSize={10} fill="#64748b" textAnchor="middle" style={{ fontWeight: 500 }}>
                {formatDate(d.date)}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

export default function AdminReports() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('sales');
  const { formatPrice } = useCurrency();

  // Sales/Orders state
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [topProducts, setTopProducts] = useState([]);
  const [loadingTop, setLoadingTop] = useState(true);

  // Chat reports state
  const [chatMetrics, setChatMetrics] = useState(null);
  const [loadingChat, setLoadingChat] = useState(false);
  const [operators, setOperators] = useState([]);
  const [selectedOperator, setSelectedOperator] = useState('');

  // Date range presets
  const today = new Date();
  const isoToday = today.toISOString().slice(0, 10);
  const defaultStart = (() => { const d = new Date(); d.setDate(d.getDate() - 13); return d.toISOString().slice(0, 10); })();
  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(isoToday);
  const [preset, setPreset] = useState('14');

  // Load Sales Data
  useEffect(() => {
    if (!hasPermission('admin.reports')) {
      navigate('/login');
      return;
    }
    if (activeTab !== 'sales') return;
    
    let mounted = true;

    async function load() {
      try {
        setLoading(true);
        const token = getToken();
        const qs = new URLSearchParams();
        if (startDate) qs.set('start', startDate);
        if (endDate) qs.set('end', endDate);
        const url = `${API_BASE_URL}/orders?${qs.toString()}`;
        const res = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
        const data = await res.json();
        if (!mounted) return;
        setOrders(data || []);
      } catch (err) {
        console.error(err);
      } finally {
        if (mounted) setLoading(false);
      }
    }

    load();
    return () => { mounted = false; };
  }, [navigate, startDate, endDate, activeTab]);

  // Load Top Products
  useEffect(() => {
    if (activeTab !== 'sales') return;
    let mounted = true;
    async function loadTop() {
      try {
        setLoadingTop(true);
        const token = getToken();
        const qs = new URLSearchParams();
        if (startDate) qs.set('start', startDate);
        if (endDate) qs.set('end', endDate);
        qs.set('limit', '10');
        const url = `${API_BASE_URL}/orders/top-products?${qs.toString()}`;
        const res = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
        const data = await res.json();
        if (!mounted) return;
        setTopProducts(data || []);
      } catch (err) {
        console.error('Failed to load top products', err);
        if (mounted) setTopProducts([]);
      } finally {
        if (mounted) setLoadingTop(false);
      }
    }
    loadTop();
    return () => { mounted = false; };
  }, [startDate, endDate, activeTab]);

  // Load Chat Metrics
  useEffect(() => {
    if (activeTab !== 'chat') return;
    let mounted = true;

    async function loadChat() {
      try {
        setLoadingChat(true);
        const token = getToken();
        const qs = new URLSearchParams();
        if (startDate) qs.set('start', startDate);
        if (endDate) qs.set('end', endDate);
        if (selectedOperator) qs.set('operator', selectedOperator);
        const url = `${API_BASE_URL}/chats/metrics?${qs.toString()}`;
        const res = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
        const data = await res.json();
        if (!mounted) return;
        setChatMetrics(data || null);
        if (data && Array.isArray(data.operators)) {
          setOperators(data.operators);
        }
      } catch (err) {
        console.error('Failed to load chat metrics', err);
      } finally {
        if (mounted) setLoadingChat(false);
      }
    }

    loadChat();
    return () => { mounted = false; };
  }, [startDate, endDate, activeTab, selectedOperator]);

  const filteredOrders = useMemo(() => {
    if (!startDate || !endDate) return orders;
    const s = new Date(startDate + 'T00:00:00Z');
    const e = new Date(endDate + 'T23:59:59Z');
    return orders.filter(o => {
      const t = new Date(o.createdAt);
      return t >= s && t <= e;
    });
  }, [orders, startDate, endDate]);

  const stats = useMemo(() => {
    const totalOrders = filteredOrders.length;
    const revenue = filteredOrders.reduce((s, o) => s + Number(o.total || 0), 0);
    return { totalOrders, revenue };
  }, [filteredOrders]);

  const lastDays = useMemo(() => {
    if (!startDate || !endDate) return [];
    const s = new Date(startDate + 'T00:00:00Z');
    const e = new Date(endDate + 'T00:00:00Z');
    const map = new Map();
    let curr = new Date(s);
    while (curr <= e) {
      const key = curr.toISOString().slice(0, 10);
      map.set(key, 0);
      curr.setDate(curr.getDate() + 1);
    }
    filteredOrders.forEach(o => {
      const k = (new Date(o.createdAt)).toISOString().slice(0, 10);
      if (map.has(k)) map.set(k, map.get(k) + Number(o.total || 0));
    });
    return Array.from(map.entries()).map(([k, v]) => ({ label: formatDate(k), value: v }));
  }, [filteredOrders, startDate, endDate]);

  const topArr = Array.isArray(topProducts) ? topProducts : [];

  return (
    <div>
      <style>{`
        .hover-card {
          transition: transform 0.2s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.2s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .hover-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.05), 0 4px 6px -2px rgba(0, 0, 0, 0.02);
        }
        .tab-btn {
          padding: 12px 24px;
          font-size: 14.5px;
          font-weight: 600;
          border: none;
          background: none;
          cursor: pointer;
          transition: all 0.25s ease;
          border-bottom: 3px solid transparent;
          outline: none;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .tab-btn.active {
          color: #2563eb;
          border-bottom-color: #2563eb;
        }
        .tab-btn:not(.active) {
          color: #64748b;
        }
        .tab-btn:not(.active):hover {
          color: #0f172a;
          background-color: #f8fafc;
          border-bottom-color: #cbd5e1;
        }
      `}</style>

      {/* Tabs Container */}
      <div style={{ display: 'flex', gap: '8px', borderBottom: '1px solid #e2e8f0', marginBottom: '24px', background: '#fff', borderRadius: '8px 8px 0 0', padding: '0 8px' }}>
        <button 
          className={`tab-btn ${activeTab === 'sales' ? 'active' : ''}`}
          onClick={() => setActiveTab('sales')}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path></svg>
          Sales & Revenue
        </button>
        <button 
          className={`tab-btn ${activeTab === 'chat' ? 'active' : ''}`}
          onClick={() => setActiveTab('chat')}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path></svg>
          Chat Support Metrics
        </button>
      </div>

      <div className="admin-grid" style={{ gridTemplateColumns: '300px 1fr' }}>
        {/* Left Column: Controls & Dynamic Stats Summary */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="admin-card">
            <div style={{ fontWeight: 600, color: '#0f172a', marginBottom: 12 }}>Date Range</div>
            <div className="admin-form-group">
              <label className="admin-label">Start Date</label>
              <input type="date" className="admin-input" value={startDate} onChange={e => { setStartDate(e.target.value); setPreset('custom'); }} />
            </div>
            <div className="admin-form-group">
              <label className="admin-label">End Date</label>
              <input type="date" className="admin-input" value={endDate} onChange={e => { setEndDate(e.target.value); setPreset('custom'); }} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 12 }}>
              <button
                className={`admin-btn ${preset === '7' ? 'admin-btn-primary' : 'admin-btn-secondary'} admin-btn-sm`}
                onClick={() => { const d = new Date(); const e = d.toISOString().slice(0, 10); d.setDate(d.getDate() - 6); const s = d.toISOString().slice(0, 10); setStartDate(s); setEndDate(e); setPreset('7'); }}
              >Last 7 Days</button>
              <button
                className={`admin-btn ${preset === '14' ? 'admin-btn-primary' : 'admin-btn-secondary'} admin-btn-sm`}
                onClick={() => { const d = new Date(); const e = d.toISOString().slice(0, 10); d.setDate(d.getDate() - 13); const s = d.toISOString().slice(0, 10); setStartDate(s); setEndDate(e); setPreset('14'); }}
              >Last 14 Days</button>
              <button
                className={`admin-btn ${preset === '30' ? 'admin-btn-primary' : 'admin-btn-secondary'} admin-btn-sm`}
                onClick={() => { const d = new Date(); const e = d.toISOString().slice(0, 10); d.setDate(d.getDate() - 29); const s = d.toISOString().slice(0, 10); setStartDate(s); setEndDate(e); setPreset('30'); }}
              >Last 30 Days</button>
              <button
                className={`admin-btn admin-btn-secondary admin-btn-sm`}
                onClick={() => { setStartDate(defaultStart); setEndDate(isoToday); setPreset('14'); }}
              >Reset</button>
            </div>
            {activeTab === 'chat' && (
              <div className="admin-form-group" style={{ marginTop: '16px', borderTop: '1px solid #f1f5f9', paddingTop: '16px' }}>
                <label className="admin-label">Filter by Operator</label>
                <select 
                  className="admin-select"
                  value={selectedOperator}
                  onChange={e => setSelectedOperator(e.target.value)}
                >
                  <option value="">All Operators</option>
                  {operators.map(op => (
                    <option key={op} value={op}>{op}</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {activeTab === 'sales' ? (
            <>
              <div className="admin-card hover-card">
                <div style={{ fontSize: 13, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', marginBottom: 4 }}>Total Orders</div>
                <div style={{ fontSize: 32, fontWeight: 800, color: '#0f172a' }}>{stats.totalOrders}</div>
              </div>
              <div className="admin-card hover-card">
                <div style={{ fontSize: 13, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', marginBottom: 4 }}>Revenue</div>
                <div style={{ fontSize: 32, fontWeight: 800, color: '#10b981' }}>{formatPrice(stats.revenue)}</div>
              </div>
            </>
          ) : (
            <>
              <div className="admin-card hover-card" style={{ borderLeft: '4px solid #3b82f6' }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', marginBottom: 4 }}>Total Chat Volume</div>
                <div style={{ fontSize: 32, fontWeight: 800, color: '#0f172a' }}>
                  {loadingChat ? '...' : (chatMetrics?.summary?.totalChats || 0)}
                </div>
              </div>
              <div className="admin-card hover-card" style={{ borderLeft: '4px solid #10b981' }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', marginBottom: 4 }}>Avg First Response Time</div>
                <div style={{ fontSize: 26, fontWeight: 800, color: '#10b981' }}>
                  {loadingChat ? '...' : formatDuration(chatMetrics?.summary?.averageFRT)}
                </div>
              </div>
              <div className="admin-card hover-card" style={{ borderLeft: '4px solid #6366f1' }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', marginBottom: 4 }}>Avg Chat Duration</div>
                <div style={{ fontSize: 26, fontWeight: 800, color: '#6366f1' }}>
                  {loadingChat ? '...' : formatDuration(chatMetrics?.summary?.averageDuration)}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Right Column: Dynamic Tabs Content */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {activeTab === 'sales' ? (
            <>
              <div className="admin-card">
                <h3 className="admin-subtitle" style={{ marginBottom: 16, color: '#1e293b', fontWeight: 700 }}>Revenue Trend ({startDate} &rarr; {endDate})</h3>
                {loading ? <div className="admin-text">Loading chart...</div> : <BarChart data={lastDays} width={800} height={280} />}
              </div>

              <div className="admin-card">
                <h3 className="admin-subtitle" style={{ marginBottom: 16, color: '#1e293b', fontWeight: 700 }}>Top Products</h3>
                <div className="admin-table-container">
                  {loadingTop ? <div className="admin-text">Loading...</div> : (
                    <table className="admin-table">
                      <thead>
                        <tr>
                          <th width="60">Img</th>
                          <th>Product</th>
                          <th width="100">Sold</th>
                          <th width="120">Revenue</th>
                        </tr>
                      </thead>
                      <tbody>
                        {topArr.length === 0 ? <tr><td colSpan="4" style={{ textAlign: 'center', color: '#64748b' }}>No products sold in this range.</td></tr> : topArr.map((p, i) => (
                          <tr key={p.id}>
                            <td>
                              <div style={{ width: 40, height: 40, background: '#f1f5f9', borderRadius: 4, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                {p.product && p.product.image ? <img src={p.product.image} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <span style={{ fontSize: 8, color: '#94a3b8' }}>N/A</span>}
                              </div>
                            </td>
                            <td>
                              <div style={{ fontWeight: 600, fontSize: 14 }}>{p.product && p.product.name ? p.product.name : 'ID #' + p.id}</div>
                            </td>
                            <td>{p.qty}</td>
                            <td>{formatPrice(p.revenue)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </>
          ) : (
            <>
              {/* Chat volume trend SVG graph */}
              <div className="admin-card">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                  <h3 className="admin-subtitle" style={{ color: '#1e293b', fontWeight: 700, margin: 0 }}>Chat Volume Trend ({startDate} &rarr; {endDate})</h3>
                  {/* Custom legend */}
                  <div style={{ display: 'flex', gap: '16px', fontSize: '12.5px', fontWeight: 600 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span style={{ display: 'inline-block', width: '12px', height: '12px', borderRadius: '3px', background: '#10b981' }} />
                      <span style={{ color: '#475569' }}>Answered</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span style={{ display: 'inline-block', width: '12px', height: '12px', borderRadius: '3px', background: '#f43f5e' }} />
                      <span style={{ color: '#475569' }}>Missed</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span style={{ display: 'inline-block', width: '12px', height: '12px', borderRadius: '3px', background: '#f59e0b' }} />
                      <span style={{ color: '#475569' }}>Offline (Leads)</span>
                    </div>
                  </div>
                </div>
                {loadingChat ? (
                  <div className="admin-text">Loading chart...</div>
                ) : (
                  <ChatVolumeChart data={chatMetrics?.dailyTrends || []} height={280} />
                )}
              </div>

              {/* Chat support breakdown details */}
              <div className="admin-card">
                <h3 className="admin-subtitle" style={{ marginBottom: 20, color: '#1e293b', fontWeight: 700 }}>Metrics Breakdown</h3>
                {loadingChat ? (
                  <div className="admin-text">Loading breakdown...</div>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px' }}>
                    <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '16px' }}>
                      <div style={{ fontSize: '13px', color: '#64748b', fontWeight: 600, textTransform: 'uppercase', marginBottom: '6px' }}>Answered/Resolved</div>
                      <div style={{ fontSize: '24px', fontWeight: 800, color: '#0f172a' }}>
                        {chatMetrics?.summary?.answeredResolvedChats || 0}
                      </div>
                      <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>
                        Chats resolved after receiving an operator response
                      </div>
                    </div>

                    <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '16px' }}>
                      <div style={{ fontSize: '13px', color: '#64748b', fontWeight: 600, textTransform: 'uppercase', marginBottom: '6px' }}>Missed / Abandoned</div>
                      <div style={{ fontSize: '24px', fontWeight: 800, color: '#f43f5e' }}>
                        {chatMetrics?.summary?.missedChats || 0}
                      </div>
                      <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>
                        Chats closed without any operator response
                      </div>
                    </div>

                    <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '16px' }}>
                      <div style={{ fontSize: '13px', color: '#64748b', fontWeight: 600, textTransform: 'uppercase', marginBottom: '6px' }}>Offline Forms (Leads)</div>
                      <div style={{ fontSize: '24px', fontWeight: 800, color: '#f59e0b' }}>
                        {chatMetrics?.summary?.offlineChats || 0}
                      </div>
                      <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>
                        Conversations initiated while offline mode was active
                      </div>
                    </div>

                    <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '16px' }}>
                      <div style={{ fontSize: '13px', color: '#64748b', fontWeight: 600, textTransform: 'uppercase', marginBottom: '6px' }}>Total Answered</div>
                      <div style={{ fontSize: '24px', fontWeight: 800, color: '#10b981' }}>
                        {chatMetrics?.summary?.answeredChats || 0}
                      </div>
                      <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>
                        Total active or closed chats answered by operators
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      <div style={{ marginTop: 24, padding: 16, borderTop: '1px solid #e2e8f0' }}>
        <p style={{ color: '#64748b', fontSize: 13, margin: 0 }}>
          Metrics filter dynamically by date range.
        </p>
      </div>
    </div>
  );
}
